import { withTimeout } from '@repo/utils'
import { type Config, getPublicClient } from '@wagmi/core'
import {
  type Address,
  type Client,
  type Hash,
  isAddressEqual,
  type Transaction,
  WaitForTransactionReceiptTimeoutError
} from 'viem'
import {
  getBlock,
  getBlockNumber,
  getTransaction,
  getTransactionCount,
  getTransactionReceipt,
  waitForTransactionReceipt
} from 'viem/actions'

import { logger } from '@/lib/observability/logger'

export interface OnchainGasLookup {
  client: Client
  hash: Hash
  from: Address
}

const ONCHAIN_GAS_LOOKUP_TIMEOUT_MS = 2_000

/** Best-effort read of the mined tx's gas limit for telemetry; never rejects. Undefined when the
 * lookup fails, times out, or the tx wasn't sent from `from` (e.g. a relayer/bundler tx). */
export async function readOnchainGasLimit({
  client,
  hash,
  from
}: OnchainGasLookup): Promise<number | undefined> {
  try {
    const tx = await withTimeout(getTransaction(client, { hash }), ONCHAIN_GAS_LOOKUP_TIMEOUT_MS)
    return isAddressEqual(tx.from, from) ? Number(tx.gas) : undefined
  } catch {
    return undefined
  }
}

interface ReceiptPollingClientParams {
  wagmiConfig: Config
  walletClient: Client
  chainId: number
  isSafe: boolean
}

/**
 * Returns the best client for polling transaction receipts.
 * Uses the app's public client (backed by eRPC) for reliable receipt polling
 * on non-Safe wallets. Safe wallets must use the wallet client because the
 * Safe connector handles safeTxHash → onchain hash resolution internally.
 */
export function getReceiptPollingClient({
  wagmiConfig,
  walletClient,
  chainId,
  isSafe
}: ReceiptPollingClientParams): Client {
  if (isSafe) return walletClient
  try {
    const pub = getPublicClient(wagmiConfig, { chainId })
    if (!pub) {
      logger.warn(
        '[tx-replace] getPublicClient returned undefined — falling back to wallet client (nonce monitor will be SKIPPED)',
        { chainId }
      )
    }
    return pub ?? walletClient
  } catch (error) {
    logger.warn(
      '[tx-replace] getPublicClient threw — falling back to wallet client (nonce monitor will be SKIPPED)',
      { chainId, extra: { error: String(error) } }
    )
    return walletClient
  }
}

/** The confirmed receipt shape the transaction context exposes to consumers. */
export type ReceiptResult = Awaited<ReturnType<typeof waitForTransactionReceipt>>

type OnReplacedCallback = NonNullable<Parameters<typeof waitForTransactionReceipt>[1]>['onReplaced']

interface WaitForReceiptParams {
  pollingClient: Client
  walletClient: Client
  hash: Hash
  /** Sender address — used as fallback for nonce monitoring when getTransaction fails */
  senderAddress: Address
  onReplaced?: OnReplacedCallback
  retryDelay: number
  timeout: number
}

/**
 * Waits for a transaction receipt with reliable replacement detection.
 *
 * Viem's built-in replacement detection relies on `getTransaction(originalHash)`
 * to learn the sender's nonce. But after a wallet speed-up, the original pending
 * tx is dropped from the mempool, making `getTransaction` return null. This
 * breaks viem's replacement detection entirely.
 *
 * Our approach:
 * 1. Immediately capture `from`/`nonce` via `getTransaction` (before the user
 *    can replace the tx — typically milliseconds after sendTransaction)
 * 2. Poll for receipt of original hash via eRPC (reliable)
 * 3. In parallel, monitor the sender's confirmed nonce via eRPC
 * 4. If nonce advances but no receipt for original hash → replacement happened
 * 5. Scan recent blocks to find the replacement tx and its receipt
 *
 * When both clients are the same (e.g. Safe wallets), falls back to viem's
 * built-in `waitForTransactionReceipt` with `onReplaced`.
 */
export async function waitForReceiptWithReplacementDetection({
  pollingClient,
  walletClient,
  hash,
  senderAddress,
  onReplaced,
  retryDelay,
  timeout
}: WaitForReceiptParams): Promise<ReceiptResult> {
  // Same client (e.g., Safe wallets) → viem's built-in detection is sufficient
  if (pollingClient === walletClient) {
    logger.debug(
      '[tx-replace] pollingClient === walletClient — using viem-only detection (no nonce monitor)',
      { extra: { hash } }
    )
    return waitForTransactionReceipt(pollingClient, {
      hash,
      onReplaced,
      retryDelay,
      timeout
    })
  }

  // Capture the nonce immediately (before user can replace).
  // Try multiple strategies: wallet client → eRPC → getTransactionCount fallback.
  let nonce: number | undefined
  try {
    const tx = await getTransaction(walletClient, { hash })
    nonce = tx.nonce
  } catch {
    // Wallet client failed — try eRPC (may have the tx cached from another upstream)
    try {
      const tx = await getTransaction(pollingClient, { hash })
      nonce = tx.nonce
    } catch {
      // Both getTransaction calls failed — we can't reliably determine the tx nonce.
      // getTransactionCount is NOT a safe fallback: if the account has earlier pending
      // txs, it returns a lower nonce, causing false-positive replacement detection
      // that resolves with the wrong receipt. Fall back to viem-only detection.
      return waitForTransactionReceipt(pollingClient, {
        hash,
        onReplaced,
        retryDelay,
        timeout
      })
    }
  }

  const from = senderAddress

  const log = (message: string, data?: Record<string, unknown>) =>
    logger.debug(`[tx-replace] ${message}`, data ? { extra: data } : undefined)

  log('starting replacement detection', { hash, from, nonce })

  return new Promise<ReceiptResult>((resolve, reject) => {
    let settled = false
    let nonceMonitorActive = true

    // Preserve Strategy 1 errors so we can propagate the original error class
    // instead of always falling back to a generic timeout error.
    let strategy1Error: unknown = undefined

    // Track consecutive nonce monitor RPC failures. We only propagate the
    // original Strategy 1 error if the backup monitor is also unhealthy when
    // the overall deadline is reached.
    let consecutiveNonceFailures = 0

    // Guard onReplaced so it fires at most once — both Strategy 1 (viem built-in)
    // and Strategy 2 (nonce monitor) can detect replacements, but consumers have
    // side effects (setPendingHash, Sentry breadcrumbs) that shouldn't duplicate.
    let replacementCallbackFired = false
    const guardedOnReplaced: typeof onReplaced = onReplaced
      ? replacement => {
          if (replacementCallbackFired) return
          replacementCallbackFired = true
          onReplaced(replacement)
        }
      : undefined

    const settle = (receipt: ReceiptResult) => {
      if (settled) return
      settled = true
      nonceMonitorActive = false
      clearTimeout(timeoutId)
      resolve(receipt)
    }

    const fail = (error: unknown) => {
      if (settled) return
      settled = true
      nonceMonitorActive = false
      clearTimeout(timeoutId)
      reject(error)
    }

    const timeoutId = setTimeout(() => {
      // At deadline, only prefer the original Strategy 1 error if the backup
      // nonce monitor is currently unhealthy too. If the nonce monitor stayed
      // healthy for the whole wait, the best classification is still a receipt
      // polling timeout rather than a stale transport error.
      if (strategy1Error && consecutiveNonceFailures > 0) {
        log('master timeout — propagating original strategy 1 error', {
          error: strategy1Error,
          consecutiveFailures: consecutiveNonceFailures
        })
        fail(strategy1Error)
      } else {
        fail(new WaitForTransactionReceiptTimeoutError({ hash }))
      }
    }, timeout)

    // Strategy 1: Standard receipt polling via eRPC (reliable, handles normal case)
    // Pass guardedOnReplaced so that if viem's built-in detection works (eRPC may
    // have the original tx cached), the UI still updates with the replacement hash.
    waitForTransactionReceipt(pollingClient, {
      hash,
      onReplaced: guardedOnReplaced,
      retryDelay,
      timeout
    })
      .then(receipt => {
        log('strategy 1 resolved (receipt polling)', {
          txHash: receipt.transactionHash
        })
        settle(receipt)
        return receipt
      })
      .catch(error => {
        // Strategy 1 failed (e.g. timeout, RPC error) — DO NOT kill the nonce
        // monitor. Let Strategy 2 continue as the backup for replacement detection.
        // But preserve the error so we can propagate it if the nonce monitor
        // also fails, instead of masking it as a generic timeout.
        strategy1Error = error
        log('strategy 1 failed, nonce monitor continues', {
          error: String(error)
        })
      })

    // Strategy 2: Nonce monitoring for replacement detection
    // Try both clients for RPC calls — eRPC may be down or rate-limited.
    const getConfirmedNonce = async (): Promise<number> => {
      try {
        return await getTransactionCount(pollingClient, {
          address: from,
          blockTag: 'latest'
        })
      } catch {
        // eRPC failed — try wallet client (Rabby, MetaMask, etc.)
        return await getTransactionCount(walletClient, {
          address: from,
          blockTag: 'latest'
        })
      }
    }

    const monitorNonce = async () => {
      // Block number recorded when nonce advance is first detected.
      // Used as the anchor for block scanning — ensures we always cover
      // the range where the replacement was mined, even on fast chains
      // (e.g. Arbitrum ~0.25s/block) where 6 blocks may be too few.
      let scanSinceBlock: bigint | undefined

      while (nonceMonitorActive) {
        await new Promise(r => setTimeout(r, retryDelay))
        if (!nonceMonitorActive) return

        try {
          const confirmedNonce = await getConfirmedNonce()
          // Reset consecutive failure counter on success
          consecutiveNonceFailures = 0

          // Nonce not yet consumed — keep polling
          if (nonce === undefined || confirmedNonce <= nonce) continue

          log('nonce advanced', { confirmedNonce, expectedNonce: nonce })

          // Nonce consumed! Check if original hash was mined (maybe eRPC found it)
          if (settled) return

          // Record the block when we first detect nonce advance.
          // The replacement was mined at or before this block.
          if (scanSinceBlock === undefined) {
            try {
              const currentBlock = await getBlockNumber(pollingClient)
              // Look back an extra 32 blocks to cover fast-chain block production
              // between the previous nonce check and this one
              scanSinceBlock = currentBlock - 32n > 0n ? currentBlock - 32n : 0n
            } catch {
              // Can't determine scan anchor — retry on next poll iteration
              log('getBlockNumber failed, deferring block scan to next cycle')
              continue
            }
          }

          try {
            const receipt = await getTransactionReceipt(pollingClient, {
              hash
            })
            // Original tx was mined — no replacement
            log('original tx mined (no replacement)', {
              txHash: receipt.transactionHash
            })
            settle(receipt)
            return
          } catch {
            // No receipt for original hash → replacement happened
            // Scan recent blocks to find the replacement tx
            log('no receipt for original hash, scanning blocks for replacement')
            // Try pollingClient first, fall back to walletClient.
            // Both wrapped in try-catch so RPC errors don't prevent fallback.
            let replacementReceipt: ReceiptResult | undefined
            try {
              replacementReceipt = await findReplacementInRecentBlocks({
                client: pollingClient,
                senderAddress: from,
                senderNonce: nonce,
                originalHash: hash,
                onReplaced: guardedOnReplaced,
                scanSinceBlock
              })
            } catch {
              log('pollingClient block scan failed, trying walletClient')
            }
            if (!replacementReceipt) {
              try {
                replacementReceipt = await findReplacementInRecentBlocks({
                  client: walletClient,
                  senderAddress: from,
                  senderNonce: nonce,
                  originalHash: hash,
                  onReplaced: guardedOnReplaced,
                  scanSinceBlock
                })
              } catch {
                log('walletClient block scan also failed')
              }
            }

            if (replacementReceipt) {
              log(
                replacementReceipt.transactionHash === hash
                  ? 'original tx found in recent blocks'
                  : 'replacement found!',
                { txHash: replacementReceipt.transactionHash }
              )
              settle(replacementReceipt)
              return
            }
            log('replacement not found in recent blocks, will retry')
            // Replacement not found in recent blocks — keep monitoring
            // (it might be in a block that hasn't been indexed yet)
          }
        } catch (monitorError) {
          // RPC error during monitoring — track consecutive failures so the
          // master timeout can tell whether the backup path was healthy.
          consecutiveNonceFailures++
          log('nonce monitor RPC error', {
            error: monitorError,
            consecutiveFailures: consecutiveNonceFailures
          })
        }
      }
    }

    monitorNonce()
  })
}

/**
 * Scans recent blocks to find a replacement transaction (same from + nonce).
 * Returns the replacement receipt if found, or undefined.
 *
 * When `scanSinceBlock` is provided, scans from the latest block back to that
 * block (capped at 128 blocks). This handles fast chains like Arbitrum where
 * the replacement may be older than 6 blocks by the time we scan.
 */
async function findReplacementInRecentBlocks(params: {
  client: Client
  senderAddress: Address
  senderNonce: number
  originalHash: Hash
  onReplaced?: OnReplacedCallback
  /** Earliest block to scan back to (set when nonce advance is first detected) */
  scanSinceBlock: bigint
}): Promise<ReceiptResult | undefined> {
  const { client, senderAddress, senderNonce, originalHash, onReplaced, scanSinceBlock } = params
  const senderLower = senderAddress.toLowerCase()

  const latestBlock = await getBlock(client, {
    includeTransactions: true
  })

  // Cap scan depth at 128 blocks to avoid excessive RPC calls
  const MAX_SCAN_DEPTH = 128n
  const effectiveMinBlock =
    latestBlock.number - MAX_SCAN_DEPTH > scanSinceBlock
      ? latestBlock.number - MAX_SCAN_DEPTH
      : scanSinceBlock

  for (
    let blockNumber = latestBlock.number;
    blockNumber >= effectiveMinBlock && blockNumber >= 0n;
    blockNumber--
  ) {
    const block =
      blockNumber === latestBlock.number
        ? latestBlock
        : await getBlock(client, {
            blockNumber,
            includeTransactions: true
          })

    // oxlint-disable-next-line consistent-type-assertions -- getBlock({ includeTransactions: true }) always returns full tx objects but the generic resolves as Hash | Transaction
    const replacementTx = (block.transactions as Transaction[]).find(
      tx => tx.from.toLowerCase() === senderLower && tx.nonce === senderNonce
    )

    if (replacementTx) {
      const receipt = await getTransactionReceipt(client, {
        hash: replacementTx.hash
      })

      // Same from+nonce also matches the original tx once it is mined but its
      // receipt still lags the block: that is not a replacement.
      if (replacementTx.hash.toLowerCase() === originalHash.toLowerCase()) return receipt

      // Determine replacement reason:
      // - "cancelled": self-transfer with 0 value (standard cancellation pattern)
      // - "replaced": default — we can't distinguish repriced vs replaced without
      //   the original tx data (which is unavailable after wallet speed-up)
      const reason: 'cancelled' | 'replaced' | 'repriced' =
        replacementTx.from.toLowerCase() === replacementTx.to?.toLowerCase() &&
        replacementTx.value === 0n
          ? 'cancelled'
          : 'replaced'

      type ReplacedParam = Parameters<NonNullable<OnReplacedCallback>>[0]
      // Partial shape: only `hash` is available for replacedTransaction since
      // the original tx was dropped from the mempool (the whole reason this
      // custom detection exists). Type assertions are unavoidable here.
      // oxlint-disable-next-line consistent-type-assertions -- partial: original tx dropped from mempool
      const replacedTx = {
        hash: originalHash
      } as ReplacedParam['replacedTransaction']
      // oxlint-disable-next-line consistent-type-assertions -- viem's Block generic doesn't narrow Transaction subtype
      const txForCallback = replacementTx as ReplacedParam['transaction']
      onReplaced?.({
        reason,
        replacedTransaction: replacedTx,
        transaction: txForCallback,
        transactionReceipt: receipt
      })

      return receipt
    }
  }

  return undefined
}
